/**
 * Theme definitions including Arcade color palette
 */

import { Theme, ThemeColor } from './theme.types';

export type { Theme, ThemeColor };

/**
 * Calculate RGB values from hex color
 */
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
}

/**
 * Calculate luminance of a color (0-1)
 */
function getLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determine contrast color (black or white) based on luminance
 */
function getContrastColor(rgb: [number, number, number]): string {
  const luminance = getLuminance(rgb);
  const whiteContrast = (1 + 0.05) / (luminance + 0.05);
  const blackContrast = (luminance + 0.05) / (0 + 0.05);
  return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
}

/**
 * Darken a color by a percentage (0-1)
 */
function darken(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  const [r, g, b] = rgb.map((val) => Math.max(0, Math.floor(val * (1 - amount))));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Lighten a color by a percentage (0-1)
 */
function lighten(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  const [r, g, b] = rgb.map((val) => Math.min(255, Math.floor(val + (255 - val) * amount)));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Create a ThemeColor object from a hex color
 */
function createThemeColor(hex: string): import('./theme.types').ThemeColor {
  const rgb = hexToRgb(hex);
  const contrast = getContrastColor(rgb);
  const contrastRgb = hexToRgb(contrast);

  return {
    value: hex,
    rgb,
    contrast,
    contrastRgb,
    shade: darken(hex, 0.15), // 15% darker
    tint: lighten(hex, 0.1), // 10% lighter
  };
}

/** When luminance heuristics disagree with brand/CSS (e.g. RMIT red → white type). */
function createThemeColorWithContrast(
  hex: string,
  contrastHex: string,
): import('./theme.types').ThemeColor {
  const rgb = hexToRgb(hex);
  const contrastRgb = hexToRgb(contrastHex);
  return {
    value: hex,
    rgb,
    contrast: contrastHex,
    contrastRgb,
    shade: darken(hex, 0.15),
    tint: lighten(hex, 0.1),
  };
}

/**
 * Arcade color palette
 */
const ARCADE_LIGHT_COLORS = {
  primary: '#22d3ee', // Aqua
  secondary: '#a855f7', // Purple
  tertiary: '#fb7185', // Rose
  success: '#34d399', // Mint
  warning: '#fbbf24', // Gold
  danger: '#f43f5e', // Hot Pink
  light: '#f2f3f3', // Light gray
  medium: '#92949c', // Medium gray
  dark: '#111111', // Dark gray
};

const ARCADE_DARK_COLORS = {
  primary: '#22d3ee', // Aqua
  secondary: '#a855f7', // Purple
  tertiary: '#fb7185', // Rose
  success: '#34d399', // Mint
  warning: '#fbbf24', // Gold
  danger: '#f43f5e', // Hot Pink
  light: '#f9fafb', // Near white
  medium: '#f3f4f6', // Near white (was #e5e7eb)
  dark: '#0f172a', // Chrome dark
};

const SUNSET_LIGHT_COLORS = {
  primary: '#f97316', // Orange
  secondary: '#f43f5e', // Rose
  tertiary: '#8b5cf6', // Violet
  success: '#22c55e', // Green
  warning: '#f59e0b', // Amber
  danger: '#ef4444', // Red
  light: '#fff7ed', // Warm light
  medium: '#a8a29e', // Warm gray
  dark: '#292524', // Warm dark
};

const SUNRISE_LIGHT_COLORS = {
  primary: '#fbbf24', // Golden Yellow
  secondary: '#fb7185', // Soft Pink
  tertiary: '#fb923c', // Peach/Orange
  success: '#22c55e', // Green
  warning: '#f59e0b', // Amber
  danger: '#ef4444', // Red
  light: '#fde68a', // Darker golden for chrome/tabs
  medium: '#a8a29e', // Warm gray
  dark: '#292524', // Warm dark
};

const RMIT_LIGHT_COLORS = {
  primary: '#E61E2A', // RMIT Red (RGB: 230, 30, 42)
  secondary: '#000054', // RMIT Blue (RGB: 0, 0, 84)
  tertiary: '#FAC800', // RMIT Yellow (RGB: 250, 200, 0)
  success: '#22c55e', // Green
  warning: '#FAC800', // RMIT Yellow (RGB: 250, 200, 0)
  danger: '#E61E2A', // RMIT Red
  light: '#E3E5E0', // RMIT Warm Grey — page canvas (see rmit-light.css)
  medium: '#5c5d66', // Muted UI on warm grey
  dark: '#000054', // RMIT Blue as semantic dark / body text
};

const VOID_COLORS = {
  primary: '#8b5cf6', // Void violet
  secondary: '#c026d3', // Arcane magenta
  tertiary: '#22d3ee', // Void teal highlight
  success: '#34d399', // Mystic green
  warning: '#f59e0b', // Amber
  danger: '#f43f5e', // Crimson
  light: '#f5f3ff', // Pale violet
  medium: '#d7d2f5', // Soft lavender
  dark: '#171327', // Lifted dark chrome
};

const DREAMGROVE_COLORS = {
  primary: '#2f855a', // Verdant green
  secondary: '#16a34a', // Emerald
  tertiary: '#84cc16', // Grove lime
  success: '#22c55e', // Leaf
  warning: '#facc15', // Sunlight
  danger: '#ef4444', // Bramble
  light: '#e9f5e4', // Morning mist
  medium: '#6b7c6f', // Sage
  dark: '#1f2d1a', // Deep grove
};

const PRIDE_COLORS = {
  primary: '#E60028', // Pride Red
  secondary: '#FF8C00', // Pride Orange
  tertiary: '#8B00FF', // Pride Violet
  success: '#00A86B', // Pride Green
  warning: '#FFD700', // Pride Gold
  danger: '#C41E3A', // Deep Red
  light: '#fff5f5', // Warm light
  medium: '#8b7d7d', // Warm gray
  dark: '#2d1b1b', // Warm dark
};

const AZURE_DEPTHS_COLORS = {
  primary: '#2563eb', // Rich Azure Blue
  secondary: '#0ea5e9', // Sky Blue
  tertiary: '#06b6d4', // Cyan
  success: '#10b981', // Emerald
  warning: '#f59e0b', // Amber
  danger: '#ef4444', // Coral Red
  light: '#f0f9ff', // Ice Blue Light
  medium: '#94a3b8', // Slate Blue Gray
  dark: '#0f172a', // Deep Ocean
};

/**
 * Default Ionic colors
 */
const DEFAULT_LIGHT_COLORS = {
  primary: '#0054e9', // Ionic Blue
  secondary: '#0163aa', // Ionic Secondary Blue
  tertiary: '#6030ff', // Ionic Purple
  success: '#2dd55b', // Ionic Green
  warning: '#ffc409', // Ionic Yellow
  danger: '#c5000f', // Ionic Red
  light: '#f4f5f8', // Light gray
  medium: '#636469', // Medium gray
  dark: '#222428', // Dark gray
};

const DEFAULT_DARK_COLORS = {
  primary: '#0054e9', // Ionic Blue
  secondary: '#0163aa', // Ionic Secondary Blue
  tertiary: '#6030ff', // Ionic Purple
  success: '#2dd55b', // Ionic Green
  warning: '#ffc409', // Ionic Yellow
  danger: '#c5000f', // Ionic Red
  light: '#f9fafb', // Near white
  medium: '#f3f4f6', // Near white (was #e5e7eb)
  dark: '#0f172a', // Chrome dark
};

/**
 * Create theme object
 */
type ThemePalette = typeof ARCADE_LIGHT_COLORS;

function createTheme(
  name: string,
  displayName: string,
  isDark: boolean,
  colorPalette: ThemePalette,
): Theme {
  return {
    name,
    displayName,
    isDark,
    colors: {
      primary: createThemeColor(colorPalette.primary),
      secondary: createThemeColor(colorPalette.secondary),
      tertiary: createThemeColor(colorPalette.tertiary),
      success: createThemeColor(colorPalette.success),
      warning: createThemeColor(colorPalette.warning),
      danger: createThemeColor(colorPalette.danger),
      light: createThemeColor(colorPalette.light),
      medium: createThemeColor(colorPalette.medium),
      dark: createThemeColor(colorPalette.dark),
    },
    // New chrome variables
    chrome: {
      background: isDark ? 'var(--ion-color-dark)' : 'var(--ion-color-light)',
      text: isDark ? 'var(--ion-color-light)' : 'var(--ion-color-dark)',
      pageBackground: isDark ? '#1e293b' : 'var(--ion-background-color)', // Specific charcoal for pages
      pageText: isDark ? 'var(--ion-color-light)' : 'var(--ion-text-color)',
      headingText: isDark ? 'var(--ion-color-light)' : 'var(--ion-text-color)',
      tabBackground: isDark ? 'var(--ion-color-dark)' : 'var(--ion-color-light)',
      tabText: isDark ? 'var(--ion-color-medium)' : 'var(--ion-color-medium)',
      tabTextSelected: isDark ? 'var(--ion-color-primary)' : 'var(--ion-color-primary)',
      mutedText: isDark ? 'var(--ion-color-medium)' : 'var(--ion-color-medium)',
    },
    // Loading card variables
    loadingCard: {
      background: isDark ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.5)',
      text: isDark ? 'var(--ion-color-light)' : 'var(--ion-text-color)',
    },
  };
}

/**
 * All available themes
 */
export const THEMES: Record<string, Theme> = {
  void: {
    name: 'void',
    displayName: 'Void',
    isDark: true,
    colors: {
      primary: createThemeColor(VOID_COLORS.primary),
      secondary: createThemeColor(VOID_COLORS.secondary),
      tertiary: createThemeColor(VOID_COLORS.tertiary),
      success: createThemeColor(VOID_COLORS.success),
      warning: createThemeColor(VOID_COLORS.warning),
      danger: createThemeColor(VOID_COLORS.danger),
      light: createThemeColor(VOID_COLORS.light),
      medium: createThemeColor(VOID_COLORS.medium),
      dark: createThemeColor(VOID_COLORS.dark),
    },
    chrome: {
      background: '#171327',
      text: '#f5f3ff',
      pageBackground: '#231d36',
      pageText: '#f5f3ff',
      headingText: '#f5f3ff',
      tabBackground: '#171327',
      tabText: 'var(--ion-color-medium)',
      tabTextSelected: 'var(--ion-color-primary)',
      mutedText: 'var(--ion-color-medium)',
    },
    loadingCard: {
      background: 'rgba(30, 30, 30, 0.7)',
      text: '#f5f3ff',
    },
  },
  dreamgrove: createTheme('dreamgrove', 'Dreamgrove', false, DREAMGROVE_COLORS),
  'arcade-light': createTheme('arcade-light', 'Arcade Light', false, ARCADE_LIGHT_COLORS),
  'arcade-dark': createTheme('arcade-dark', 'Arcade Dark', true, ARCADE_DARK_COLORS),
  'sunset-light': createTheme('sunset-light', 'Sunset', false, SUNSET_LIGHT_COLORS),
  'sunrise-light': createTheme('sunrise-light', 'Sunrise', false, SUNRISE_LIGHT_COLORS),
  'rmit-light': (() => {
    const base = createTheme('rmit-light', 'RMIT', false, RMIT_LIGHT_COLORS);
    return {
      ...base,
      colors: {
        ...base.colors,
        // Match rmit-light.css; auto contrast can pick black on saturated red.
        primary: createThemeColorWithContrast(RMIT_LIGHT_COLORS.primary, '#ffffff'),
        danger: createThemeColorWithContrast(RMIT_LIGHT_COLORS.danger, '#ffffff'),
      },
    };
  })(),
  pride: createTheme('pride', 'Pride', false, PRIDE_COLORS),
  'azure-depths': {
    name: 'azure-depths',
    displayName: 'Azure Depths',
    isDark: false,
    colors: {
      primary: createThemeColor(AZURE_DEPTHS_COLORS.primary),
      secondary: createThemeColor(AZURE_DEPTHS_COLORS.secondary),
      tertiary: createThemeColor(AZURE_DEPTHS_COLORS.tertiary),
      success: createThemeColor(AZURE_DEPTHS_COLORS.success),
      warning: createThemeColor(AZURE_DEPTHS_COLORS.warning),
      danger: createThemeColor(AZURE_DEPTHS_COLORS.danger),
      light: createThemeColor(AZURE_DEPTHS_COLORS.light),
      medium: createThemeColor(AZURE_DEPTHS_COLORS.medium),
      dark: createThemeColor(AZURE_DEPTHS_COLORS.dark),
    },
    chrome: {
      background: 'var(--ion-color-light)', // Ice blue light for chrome
      text: 'var(--ion-color-dark)',
      pageBackground: '#eff6ff', // Slightly deeper blue-white for pages
      pageText: 'var(--ion-text-color)',
      headingText: 'var(--ion-text-color)',
      tabBackground: 'var(--ion-color-light)',
      tabText: 'var(--ion-color-medium)',
      tabTextSelected: 'var(--ion-color-primary)',
      mutedText: 'var(--ion-color-medium)',
    },
    loadingCard: {
      background: 'rgba(255, 255, 255, 0.5)',
      text: 'var(--ion-text-color)',
    },
  },
  'default-light': createTheme('default-light', 'Default Light', false, DEFAULT_LIGHT_COLORS),
  'default-dark': createTheme('default-dark', 'Default Dark', true, DEFAULT_DARK_COLORS),
};

/**
 * Get theme by name
 */
export function getTheme(name: string): Theme | undefined {
  return THEMES[name];
}

/**
 * Get all available theme names
 */
export function getAvailableThemes(): string[] {
  return Object.keys(THEMES);
}
